import { Container, formatDate, Separator } from '@amelio/ui-library';

import { EmptyResults } from './EmptyResults/EmptyResults';
import { ResultsContent } from './ResultsContent/ResultsContent';
import { ResultsHeader } from './ResultsHeader/ResultsHeader';

import type { User } from '@/types/directory/User';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { FeedbackResultsArrowIllustration } from '@/components/Illustrations/Illustration';
import {
    QuestionResultContainer,
    type QuestionResultData,
} from '@/components/QuestionResultContainer';
import { QuestionType } from '@/constants/QuestionType';
import { ReviewType } from '@/constants/review/CycleInstance';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import i18n from '@/plugins/i18n';
import { getReviewTypeLabel } from '@/utils/cycleInstance';
import { getFullName } from '@/utils/user';

export interface ResultsProps {
    reviewInstance: ReviewInstance;
    reviewee: User;
    reviewers: User[];
    questions: QuestionResultData[];
    onReplySubmit?: (reviewQuestionAnswerId: string, content: string) => Promise<void> | void;
    isSubmittingReply?: boolean;
}

export const Results = ({
    reviewInstance,
    reviewee,
    reviewers,
    questions,
    onReplySubmit,
    isSubmittingReply = false,
}: ResultsProps) => {
    const { getJobTitle } = useCompanyInformation();
    const reviewType = reviewInstance.cycleInstance?.reviewType ?? ReviewType.EVALUATION_360;

    return (
        <Container
            direction={'col'}
            className={'p-sm-18 w-full p-10'} // p-18 intentional: between '2xl' (p-16) and 'page' (p-22)
        >
            <div className={'relative'}>
                <ResultsHeader
                    employeeName={getFullName(reviewee)}
                    employeeRole={getJobTitle(reviewee.jobTitleId || '')}
                    evaluationLabel={getReviewTypeLabel(reviewType)}
                    invitedCount={reviewInstance.totalCount}
                    respondedCount={reviewInstance.responseCount}
                    reviewers={reviewers}
                    dateLabel={formatDate({
                        date: reviewInstance.completedAt,
                        language: i18n.language,
                    })}
                    reviewType={reviewType}
                />

                <FeedbackResultsArrowIllustration
                    className={'relative -top-14 left-29 -mb-56 sm:left-20 md:left-75 xl:left-105'}
                />
            </div>

            {questions.length < 1 ? (
                <EmptyResults
                    reviewType={reviewType}
                    employeeFirstName={reviewee.firstName}
                />
            ) : (
                <>
                    <ResultsContent>
                        {questions.map((questionData, index) => {
                            const questionId = questionData.question.id as string;
                            const isLastQuestion = index === questions.length - 1;

                            if (questionData.question.questionType === QuestionType.SECTION_TITLE) {
                                return null;
                            }

                            return (
                                <div key={questionId}>
                                    <QuestionResultContainer
                                        data={questionData}
                                        onReplySubmit={onReplySubmit}
                                        isSubmittingReply={isSubmittingReply}
                                        reviewee={reviewee}
                                    />

                                    {!isLastQuestion && (
                                        <Separator
                                            spacing={'xl'}
                                            color={'base-200'}
                                            dataTestId={`results-separator-${index}`}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </ResultsContent>
                </>
            )}
        </Container>
    );
};

export default Results;
